import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Splitter } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";
import { Button } from "@progress/kendo-react-buttons";
import { LoadingPanel } from "../../LoadingPanel";
import { AdministratorVenue } from "./Venue";
import { Checkbox, TextBox, InputClearValue  } from "@progress/kendo-react-inputs";
import { Pager } from "@progress/kendo-react-data-tools";
import { Icon } from "@progress/kendo-react-common";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { ScreenSize } from '../../../app/ScreenSize';
import { GetScreenSize } from '../../../app/GetScreenSize';
import { getCSRFToken } from '../../../app/getCSRFToken';
import useSpeechToText from 'react-hook-speech-to-text';

import './Venues.scss';

export const AdministratorVenues = () => {
    const dispatch = useDispatch();

    const [venues, setVenues] = useState([]);
    const [venuesPagerData, setVenuesPagerData] = useState({
        total: 0,
        skip: 0,
        take: 1
    });
    const [selectedVenueKey, setSelectedVenueKey] = useState(null);
    const [venue, setVenue] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [doCreateNewVenue, setDoCreateNewVenue] = useState(false);
    const [venueNameFilterValue, setVenueNameFilterValue] = useState("");
    const [onlyActiveVenuesValue, setOnlyActiveVenuesValue] = useState(true);
    const [venueLoading, setVenueLoading] = useState(false);
    const [regions, setRegions] = useState([]);
    const [countries, setCountries] = useState([]);

    const typingTimeout = useRef();
    const typingTimeoutDelay = 500; //Milliseconds

    const {
        error,
        results,
        isRecording,
        startSpeechToText,
        stopSpeechToText,
    } = useSpeechToText({
        timeout: 1000
    });

    const standardPanes = [
        {
            size: "340px",
            min: "5%",
            resizable: false,
        },
        {}
    ];

    const getMobilePanes = () => {
        if (!venue && doCreateNewVenue === false) {
            return [
                {
                    size: "100%",
                    resizable: false,
                },
                {
                    collapsed: true,
                }
            ];
        }

        return [
            {
                size: "100%",
                resizable: false,
                collapsed: true,
            },
            {
                              
            }
        ];
    }

    const renderVenueListItem = (props) => {
        let item = props.dataItem;
        
        return (
            <div className={"venueListItem" + (item.venueKey === selectedVenueKey ? " venueListItemSelected" : "")} onClick={() => selectVenue(item.venueKey)}>
                <div className="venueListPrimaryText">
                    {item.currentName}
                </div>
                <div className="venueListSecondaryText">
                    {item.isActive ? "Active" : "Inactive"}
                </div>                
            </div>
        );
    }

    const selectVenue = (venueKey) => {
        getVenue(venueKey);
    }

    const createNewVenue = () => {
        setVenue(undefined);
        setSelectedVenueKey(null);
        setVenueLoading(false);
        setDoCreateNewVenue(true);

        getVenues(venueNameFilterValue, onlyActiveVenuesValue, venuesPagerData.skip, venuesPagerData.take);
    }

    const clearVenue = () => {
        setVenue(undefined);
        setSelectedVenueKey(null);
        setVenueLoading(false);
        setDoCreateNewVenue(false);

        getVenues(venueNameFilterValue, onlyActiveVenuesValue, venuesPagerData.skip, venuesPagerData.take);
    }

    const getVenue = (venueKey) => {
        setSelectedVenueKey(venueKey);
        setVenueLoading(true);
        setDoCreateNewVenue(false);

        const dataItem = {
            venueKey: venueKey,
        };

        fetch(`/api/administrator/getvenue`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setVenue(result);
                setVenueLoading(false);

                getVenues(venueNameFilterValue, onlyActiveVenuesValue, venuesPagerData.skip, venuesPagerData.take);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setVenue(undefined);
                setVenueLoading(false);
            });
    }

    const getVenues = (name, onlyActive, skip, take) => {
        if (!skip) {
            skip = 0;
        }

        if (!take) {
            take = 1;
        }

        const pageNumber = (skip / take) + 1;

        const dataItem = {
            name: name,
            onlyActive: onlyActive,
            pageNumber: pageNumber,
        };

        fetch(`/api/administrator/venues`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setLoading(false);
                setVenues(result.venues);
                setVenuesPagerData({
                    total: result.totalVenueCount,
                    skip: (result.pageNumber - 1) * result.venuesPerPage,
                    take: result.venuesPerPage
                });
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
                
                setLoading(false);
                setLoadingError(error.message);
            });        
    }

    const handleVenuePageChange = (event) => {
        const { skip, take } = event;

        setVenuesPagerData({
            total: venuesPagerData.total,
            skip: skip,
            take: take
        });

        getVenues(venueNameFilterValue, onlyActiveVenuesValue, skip, take);
    };

    const venueNameFilterValueChangedEvent = (event) => {
        const value = event.value;
        venueNameFilterValueChanged(value);
    }

    const venueNameFilterValueChanged = (value) => {
        clearTimeout(typingTimeout.current);
        setVenueNameFilterValue(value);

        typingTimeout.current = setTimeout(() => {
            getVenues(value, onlyActiveVenuesValue);
        }, typingTimeoutDelay);
    }

    const venueNameClearFilterValue = () => {
        setVenueNameFilterValue("");
        getVenues("", onlyActiveVenuesValue);
    }

    const onlyActiveVenuesValueChanged = (event) => {
        const value = event.value;

        setOnlyActiveVenuesValue(value);
        getVenues(venueNameFilterValue, value);
    }

    const getRegions = () => {
        fetch(`/api/general/regions`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                result.unshift({
                    regionIsoCode: "",
                    twoLetterIsoCountryCode: "",
                    name: "No Region",
                    countryName: "None",
                });
                setRegions(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setLoadingError(error.message);
            });
    }

    const getCountries = () => {
        fetch(`/api/general/countries`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                setCountries(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setLoadingError(error.message);
            });
    }

    useEffect(() => {
        document.title = "Venues";

        getCountries();
        getRegions();
        getVenues(venueNameFilterValue, onlyActiveVenuesValue);
    }, []);

    useEffect(() => {
        if (!isRecording) {
            if (results.length > 0) {
                const speechValue = results.pop();
                venueNameFilterValueChanged(speechValue); 
            }
        }
    }, [isRecording]);
        
    return (
        <Splitter                
            panes={GetScreenSize() === ScreenSize.Mobile ? getMobilePanes() : standardPanes}
        >                
            <div>
                <div className="venueListFilterHeader">
                    <Button icon="plus" themeColor={"primary"} onClick={createNewVenue}>Create New Venue</Button>
                </div>
                <div className="venueListFilter">
                    <div className="d-flex flex-row">
                        <TextBox
                            type={"text"}
                            maxLength={"50"}
                            value={venueNameFilterValue}
                            onChange={venueNameFilterValueChangedEvent}
                            placeholder={"Search part of venue name"}   
                            suffix={() => (
                                <>
                                    {venueNameFilterValue !== "" && (
                                        <InputClearValue onClick={venueNameClearFilterValue}>
                                            <Icon name="x" />
                                        </InputClearValue>
                                    )}                                
                                </>
                            )}
                        />

                        {!error && <Button className={isRecording ? "recordButton recording pt-2 pb-0 ms-2" : "recordButton pt-2 pb-0 ms-2"} iconClass="fa-solid fa-microphone" onClick={isRecording ? stopSpeechToText : startSpeechToText} />}
                    </div>
                    <div>
                        <Checkbox
                            label={"Only active"}
                            onChange={onlyActiveVenuesValueChanged}
                            value={onlyActiveVenuesValue}
                        />
                    </div>
                </div>
                {loading ? <LoadingPanel /> : (loadingError === "" ? (
                    <>
                        <ListView
                            data={venues}
                            item={renderVenueListItem}
                        />
                        <Pager
                            skip={venuesPagerData.skip}
                            take={venuesPagerData.take}
                            total={venuesPagerData.total}
                            buttonCount={0}
                            info={true}
                            type={"numeric"}
                            previousNext={true}
                            onPageChange={handleVenuePageChange}
                        />
                    </>) : <div></div>)}
            </div>
            <AdministratorVenue
                venue={venue}
                loading={venueLoading}
                createNewVenue={doCreateNewVenue}
                cancel={clearVenue}
                selectVenue={selectVenue}
                regions={regions}
                countries={countries}
            />
        </Splitter>
    );
}